import React, { memo, useState } from "react";
import { supportedWidgets } from "@api/schema/supportedWidgets";
import { Box, Button, DialogActions, DialogContent, DialogTitle, Divider, FormHelperText, FormLabel, IconButton, Input, List, ListItem, Modal, ModalDialog, ModalOverflow, Radio, RadioGroup, Tab, TabList, TabPanel, Tabs, Tooltip, Typography, tabClasses } from "@mui/joy";
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Controller, FormProvider, useForm } from "react-hook-form";
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SearchSelect from "@components/SearchSelect";
import { isNumeric } from "@utils/numbers";
import { useDashboard } from "@providers/DashboardProvider";

const ConfigureWidget = memo(function ConfigureWidget({
    dashboard,
    widget = null,
    updateWidget,
    activateButton = null,
    isEditing = null,
    ...restProps
}) {
    const [open, setOpen] = useState(false);
    const [currentTab, setCurrentTab] = useState(widget?.i ? 1 : 0);
    const {isConfiguringWidget, setIsConfiguringWidget} = useDashboard();

    let defaultValues = {
        title: widget?.title,
        type: widget?.type,
        options: widget?.options,
    };

    const formMethods = useForm({
        mode: 'onTouched',
        defaultValues,
    });
    const {
          register,
          unregister,
          setValue,
          reset,
          handleSubmit,
          watch,
          getValues,
          control,
          formState: { isSubmitting, errors, isValid, isDirty }
    } = formMethods;

    const openHandler = () => {
        setOpen(true);
        setIsConfiguringWidget(true);
    }

    const closeHandler = () => {
        reset();
        setCurrentTab(widget?.i ? 1 : 0);
        setOpen(false);
        setIsConfiguringWidget(false);
    }

    const selectedType = watch('type');
    const selectedOptions = watch('options');
    const selectedOptionType = selectedOptions?.type;

    const selectedWidget = supportedWidgets?.[selectedType];
    const selectedWidgetType = selectedWidget?.[selectedOptionType];

    const isConfigurable = /*() => {
        return*/ !(
            selectedWidget &&
            !selectedWidgetType?.options
            && selectedType != 'workplan'
            && selectedOptionType != 'tasklist'
        );
    //}

    const onSubmit = (fields) => {
        let _newWidget = {
            ...widget,
            ...fields,
        };

        if(!widget?.i) {
            // add default w/h dimensions
            _newWidget.w = selectedWidgetType.w ?? 2;
            _newWidget.h = selectedWidgetType.h ?? 2;
        }

        delete _newWidget.widget; // in case the widget gets applied to itself

        updateWidget(_newWidget);

        closeHandler();
    };

    const widgetHasOption = function(optionType) {
        // Make sure that the widget exists
        if(!selectedWidget || !selectedWidgetType)
        {
            return false;
        }

        // Check for custom widgets (work plan task list)
        // Todo: figure out how to move this logic into the widget config object
        if(
            selectedType == 'workplan'
            && selectedOptionType == 'tasklist'
            && selectedOptions?.hasOwnProperty('viewType')
            && selectedOptions?.viewType == 'contact'
            && optionType == 'contacts'
        )
        {
            return true;
        }

        // Check that it has configurable options
        if(
            !selectedWidgetType.hasOwnProperty('options')
        )
        {
            return false;
        }

        const allOptions = selectedWidgetType.options;

        // Handle custom widget properties
        if(
            selectedOptionType == 'owner'
            && ['business', 'contact'].indexOf(optionType) !== -1
        ) {
            if(allOptions.indexOf('contact_or_business_radio') !== -1) {

                let contactBusinessRadioVal = selectedOptions.viewType;//'contact';
                if(contactBusinessRadioVal === optionType) {
                    return true;
                }
            }

            return false;
        }

        // Standard widget
        return allOptions.indexOf(optionType) != -1;
    };

    const isFormValid = () => {
        // make sure a widget type is chosen
        if(!selectedType) {
            return false;
        }

        // if there's no config required, we're good
        if(!isConfigurable) {
            return true;
        }

        const objectKeys = Object.keys(selectedOptions);

        if(
            objectKeys.includes('viewType') && selectedOptions.viewType === 'all'
            ||
            objectKeys.includes('name') && selectedOptions.name
        ) {
            return true;
        }

        // ensure the key exists
        if(
            (
                !objectKeys.includes('id')
                && !objectKeys.includes('ids')
                && !objectKeys.includes('viewType')
            )
            ||
            (
                objectKeys.includes('viewType')
                && objectKeys.viewType !== 'all'
                && !objectKeys.includes('id')
                && !objectKeys.includes('ids')
            )
        ) {
            return false;
        }

        // ensure the key has a value
        for(const key of objectKeys) {
            if(key === 'id' && !selectedOptions[key]) {
                return false;
            }
            if(key === 'ids' && (!selectedOptions[key] || !selectedOptions[key].length)) {
                return false;
            }
        }

        return true;
    }

    const ViewTypeRadio = ({label, value, ...restProps}) => (
        <Radio
            checked={selectedOptions?.viewType === value}
            onChange={(e) => {
                const objectKeys = Object.keys(selectedOptions);
                // null out the ID(s) first
                if(objectKeys.includes('ids')) {
                    setValue('options.ids', null);
                }
                if(objectKeys.includes('id')) {
                    setValue('options.id', null);
                }
                setValue('options.viewType', value);
            }}
            name={'viewType'}
            value={value}
            label={label}
            required
            {...restProps}
        />
    );

    const ViewTypeSearchSelect = ({
        singularKey,
        pluralKey = null,
        endpoint,
    }) => {
        // Choose whether to render
        if(
            !widgetHasOption(singularKey)
            &&
            (
                !pluralKey
                ||
                (pluralKey && !widgetHasOption(pluralKey))
            )
        ) {
            return null;
        }

        const isMultiple = widgetHasOption(pluralKey);

        const { name } = register(isMultiple ? 'ids' : 'id', {
            required: true
        });

        // <Controller></Controller>
        return (
            <Controller
            name={`options.${isMultiple ? 'ids' : 'id'}`}
            control={control}
            // rules={{ required: true }}
          // defaultValue={false}
          render={({ field: { onChange, onBlur, value:registerValue } }) => (
            <SearchSelect
                endpoint={endpoint}
                multiple={isMultiple}
                required
                onBlur={onBlur}
                canCreate={false}
                // hydrateWith={}
                onChange={(id, value) => {
                    let newValue = isMultiple ? value.map(val => isNumeric(val) ? val : val.id) : id;
                    // console.log(id, value, newValue);
                    setValue(`options.${isMultiple ? 'ids' : 'id'}`, newValue);
                }}
                value={isMultiple ? selectedOptions.ids : selectedOptions.id}
                // value={registerValue}
                // name={name}
            />
        )}
        />)
    }


    const Btn = activateButton;
    return (<>
        {activateButton !== null && <Btn {...restProps} onClick={openHandler}/>}
        {activateButton === null && (
            <Tooltip title={`${widget ? 'Edit' : 'Add'} Widget`}>
                <IconButton
                    size="sm"
                    sx={{
                        transition: 'var(--mm-default-transition)',
                        '--IconButton-size': '1.8rem',
                        ':not(:hover)': {
                            opacity: 0.6
                        },
                        ':hover': {
                            '--variant-plainHoverBg': 'var(--joy-palette-neutral-200)',
                            // var(--variant-plainHoverBg, var(--joy-palette-neutral-plainHoverBg, var(--joy-palette-neutral-100, #F0F4F8)))
                        }
                    }}
                    {...restProps}
                    onClick={openHandler}
                >
                    {widget ? <SettingsRoundedIcon /> : <AddRoundedIcon />}
                </IconButton>
            </Tooltip>
        )}

        <Modal open={open}
         >
            <ModalOverflow>
            <form onSubmit={handleSubmit(onSubmit)}>
            <FormProvider {...formMethods}>
                <ModalDialog sx={{width: '100%', maxWidth: '600px'}}>
                    <DialogTitle>
                        Configure Widget
                    </DialogTitle>
                    <Divider/>

                    <DialogContent
                     sx={{
                     marginInline: 'var(--Divider-inset)',
                     paddingInline: 'calc(-1 * var(--Divider-inset))'
                }}>
                        <Tabs
                            value={currentTab}
                            onChange={(e, newTab) => {
                                // const index = e.target.getAttribute('tabindex');
                                // index && setCurrentTab(parseInt(index));
                                setCurrentTab(newTab);
                            }}
                            sx={{ bgcolor: 'transparent' }}
                        >
                            <TabList
                                disableUnderline
                                sx={{
                                    position: 'sticky',
                                    top: 0,
                                    p: 0.5,
                                    gap: 0.5,
                                    borderRadius: 'xl',
                                    bgcolor: 'background.level1',
                                    [`& .${tabClasses.root}[aria-selected="true"]`]: {
                                        boxShadow: 'sm',
                                        bgcolor: 'background.surface',
                                    },
                                }}
                            >
                                <Tab disableIndicator>Choose Widget</Tab>
                                <Tab
                                    disableIndicator
                                    disabled={!watch('type')}
                                >Settings</Tab>
                            </TabList>
                            <Box>
                                <TabPanel value={0} sx={{p: 0}}>
                                    <RadioGroup
                                    // {...register('type')}
                                    >
                                        <List
                                            sx={{
                                                '--List-gap': '0.5rem',
                                                '--ListItem-paddingY': '1rem',
                                                '--ListItem-radius': '8px',
                                                '--ListItemDecorator-size': '32px',
                                            }}
                                        >
                                            {Object.keys(supportedWidgets).map((widgetTypeKey, widgetTypeIndex) => (<React.Fragment key={widgetTypeKey}>
                                                {Object.keys(supportedWidgets[widgetTypeKey]).map((widgetKey, widgetId) => {
                                                    const widgetOption = supportedWidgets[widgetTypeKey][widgetKey];
                                                    return (
                                                        <ListItem
                                                            key={`${widgetTypeKey}-${widgetKey}`}
                                                            sx={{
                                                                boxShadow: 'sm',
                                                                // flexGrow: 1,
                                                                flexDirection: 'row-reverse',
                                                                justifyContent: 'space-between',
                                                            }}
                                                        >
                                                            <Radio
                                                                overlay
                                                                checkedIcon={<CheckCircleRoundedIcon />}
                                                                onClick={() => {
                                                                    if( watch('type') === widgetTypeKey && watch('options')?.type === widgetKey) {
                                                                        setCurrentTab(1);
                                                                    }
                                                                }}
                                                                onChange={() => {
                                                                    setValue('type', widgetTypeKey);

                                                                    const options = {
                                                                        type: widgetKey,
                                                                    };

                                                                    setValue('options', options);

                                                                    setCurrentTab(1);
                                                                }}
                                                                checked={
                                                                    watch('type') === widgetTypeKey &&
                                                                    watch('options')?.type === widgetKey
                                                                }
                                                                value={`${widgetTypeKey}__${widgetKey}`}
                                                                // label={widgetOption.name}
                                                                sx={{
                                                                    // flexGrow: 1,
                                                                    // flexDirection: 'row-reverse',
                                                                }}
                                                            />
                                                            <div>
                                                                <FormLabel sx={{fontWeight: 'bold'}}>{widgetOption.name}</FormLabel>
                                                                <FormHelperText>{widgetOption.desc}</FormHelperText>
                                                            </div>
                                                            {/* <FormHelperText>{widgetOption.desc}</FormHelperText> */}
                                                        </ListItem>
                                                    )
                                                })}
                                            </React.Fragment>))}
                                            {/*  */}
                                        </List>
                                    </RadioGroup>
                                </TabPanel>
                                <TabPanel value={1}>
                                    <Box>
                                    {isConfigurable === true && (<>
                                        {/* Custom - Work plan task list */}
                                        {selectedType === 'workplan' && selectedOptionType === 'tasklist' && (<>
                                        <RadioGroup sx={{mb: 2}}>
                                            <ViewTypeRadio
                                                value={'all'}
                                                label="View all tasks"
                                            />
                                            <ViewTypeRadio
                                                value={'contact'}
                                                label="View a specific person(s) tasks"
                                            />
                                        </RadioGroup>
                                        </>)}

                                        {/* Custom - Biz/Contact Radio */}
                                        {selectedOptionType === 'owner' && widgetHasOption('contact_or_business_radio') && (<>
                                        <RadioGroup sx={{mb: 2}}>
                                            <ViewTypeRadio
                                                value={'business'}
                                                label="Business"
                                            />
                                            <ViewTypeRadio
                                                value={'contact'}
                                                label="Contact"
                                            />
                                        </RadioGroup>
                                        </>)}

                                        <ViewTypeSearchSelect
                                            singularKey={'contact'}
                                            pluralKey={'contacts'}
                                            endpoint={'contacts'}
                                        />

                                        <ViewTypeSearchSelect
                                            singularKey={'property'}
                                            endpoint={'properties'}
                                        />

                                        <ViewTypeSearchSelect
                                            singularKey={'business'}
                                            pluralKey={'businesses'}
                                            endpoint={'businesses'}
                                        />

                                        <ViewTypeSearchSelect
                                            singularKey={'workplan'}
                                            pluralKey={'workplans'}
                                            endpoint={'work-plans'}
                                        />

                                        {/*
                                        {(widgetHasOption('contact') || widgetHasOption('contacts')) && (<>
                                            <SearchSelect
                                                endpoint="contacts"
                                                multiple={widgetHasOption('contacts') ? true : false}
                                                // hydrateWith={}
                                                onChange={(id, value) => {
                                                    let ids = value.map(val => val.id);
                                                    setValue(`options.${widgetHasOption('contacts') ? 'ids' : 'id'}`, value);
                                                }}
                                                value={widgetHasOption('contacts') ? selectedOptions.ids : selectedOptions.id}
                                            />
                                        </>)}
                                        */}

                                        {widgetHasOption('name') && (
                                            <Input
                                                {...register('name', {
                                                    required: true
                                                })}
                                                variant="outlined"
                                                value={selectedOptions?.name}
                                                onChange={(event) => {
                                                    setValue('options.name', event.target.value);
                                                }}
                                                type={'text'}
                                                startDecorator={'Name'}
                                                placeholder="e.g. Office Spring Cleaning"
                                                sx={{
                                                    width: '100%',
                                                    minWidth: '100px',
                                                }}
                                                required
                                                // {...register(name, config)}
                                                // defaultValue={defaultValue}
                                                // {...restProps}
                                            />
                                        )}


                                    </>)}
                                    {isConfigurable !== true && (
                                        <Typography>This widget doesn't require any configuration.</Typography>
                                    )}

                                    </Box>
                                </TabPanel>
                            </Box>
                        </Tabs>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            color="primary"
                            variant="solid"
                            disabled={!isFormValid()}
                            type="submit"
                            onClick={() => {
                                onSubmit(getValues());
                            }}
                        >
                            Save
                        </Button>
                        <Button
                            variant="plain"
                            color="neutral"
                            onClick={() => {
                                closeHandler();
                                try {
                                    // onCancel();
                                } catch(e) {
                                    //
                                }
                            }}
                        >
                            Cancel
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </FormProvider>
            </form>
            </ModalOverflow>
        </Modal>
    </>);
});

export default ConfigureWidget;
